import { useState, useEffect, useCallback, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Search, RefreshCw, Sparkles, Check, Image as ImageIcon, Upload, X, Loader2, Plus } from 'lucide-react';
import { mediaApi } from '@/lib/mediaApi';
import api from '@/lib/api';
import { toast } from 'sonner';
import { OrgMatchDialog, PersonMatchDialog } from '@/components/EntityMatchDialog';

const IMAGE_TYPES = ['uploaded', 'stock', 'ai_generated', 'logo', 'licensed'];

const EMPTY_BROWSE_FILTERS = { search: '', image_type: '', orientation: '', tags: '' };

function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

function EntitySearchCombobox({ entityType, value, onSelect, onClear, placeholder, onCreateRequest }) {
  const [query, setQuery]     = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen]       = useState(false);
  const debouncedQuery        = useDebounce(query, 150);
  const wrapperRef            = useRef(null);
  const cache                 = useRef({});

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!debouncedQuery || debouncedQuery.length < 2) { setResults([]); setLoading(false); return; }
    const cacheKey = `${entityType}:${debouncedQuery.toLowerCase()}`;
    if (cache.current[cacheKey]) { setResults(cache.current[cacheKey]); return; }
    setLoading(true);
    const endpoint = entityType === 'organisation' ? '/organisations/match' : '/persons/match';
    api.get(endpoint, { params: { q: debouncedQuery } })
      .then(res => { const d = res.data || []; cache.current[cacheKey] = d; setResults(d); })
      .catch(() => setResults([]))
      .finally(() => setLoading(false));
  }, [debouncedQuery, entityType]);

  if (value) {
    return (
      <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
        <div>
          <p className="text-sm font-medium text-foreground">{value.name}</p>
          {value.title && <p className="text-xs text-muted-foreground">{value.title}</p>}
        </div>
        <button onClick={onClear} className="text-muted-foreground hover:text-red-500 ml-2">
          <X size={13} />
        </button>
      </div>
    );
  }

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder || `Search ${entityType}s...`}
          className="w-full pl-8 pr-8 py-2 border border-border rounded-lg text-sm bg-background text-foreground placeholder:text-muted-foreground"
        />
        {loading
          ? <Loader2 size={13} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-muted-foreground" />
          : query && <button onClick={() => { setQuery(''); setResults([]); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"><X size={13} /></button>
        }
      </div>
      {open && query.length >= 2 && (
        <div className="absolute z-50 mt-1 w-full bg-background border border-border rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {loading && (
            <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground">
              <Loader2 size={13} className="animate-spin" /> Searching...
            </div>
          )}
          {!loading && results.length === 0 && (
            <div className="px-3 py-2 text-sm text-muted-foreground">No results found</div>
          )}
          {!loading && results.map(r => (
            <button
              key={r.id}
              onClick={() => { onSelect(r); setQuery(''); setOpen(false); setResults([]); }}
              className="w-full text-left px-3 py-2 hover:bg-muted text-sm flex items-center justify-between gap-2"
            >
              <div className="min-w-0">
                <span className="font-medium">{r.name}</span>
                {r.title && <span className="text-xs text-muted-foreground ml-2">{r.title}</span>}
              </div>
              <span className="text-xs text-muted-foreground shrink-0">{Math.round((r.confidence || 0) * 100)}%</span>
            </button>
          ))}
          {!loading && query.trim().length >= 2 && onCreateRequest && (
            <button
              onClick={() => { onCreateRequest(query.trim()); setOpen(false); setQuery(''); setResults([]); }}
              className="w-full text-left px-3 py-2 hover:bg-[#FFF4E8] text-sm flex items-center gap-2 border-t border-border text-blue-600"
            >
              <Plus size={13} /> Create "{query.trim()}"
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function AssetThumb({ asset, selected, onSelect }) {
  return (
    <div
      onClick={() => onSelect(asset)}
      className={`relative cursor-pointer rounded-lg overflow-hidden border transition-all ${
        selected ? 'border-blue-500 ring-2 ring-blue-200' : 'border-border hover:border-[#C9B49A]'
      }`}
    >
      <div className="aspect-video bg-muted">
        <img
          src={asset.thumbnail_url || asset.file_url}
          alt={asset.alt_text || asset.filename}
          className="w-full h-full object-cover"
          onError={(e) => { e.target.src = '/placeholder-image.png'; }}
        />
        {selected && (
          <div className="absolute inset-0 bg-blue-500/20 flex items-center justify-center">
            <Check size={20} className="text-blue-600 bg-white rounded-full p-1" />
          </div>
        )}
      </div>
      <div className="p-1.5">
        <p className="text-xs text-foreground truncate">{asset.filename}</p>
        <p className="text-xs text-muted-foreground">Used {asset.usage_count}x</p>
      </div>
    </div>
  );
}

// AI Generate tab inside MediaPickerModal
function AIGenerateTab({ aiImageCaption, aiImageAltText, aiImageTags, aiImageAccreditation, aiPromptSuggestion, organisationId, organisationName, personId, personName, onSelect }) {
  const [prompt, setPrompt]             = useState(aiPromptSuggestion || '');
  const [generating, setGenerating]     = useState(false);
  const [saving, setSaving]             = useState(false);
  const [preview, setPreview]           = useState(null);
  const [orgDialog, setOrgDialog]       = useState(false);
  const [personDialog, setPersonDialog] = useState(false);
  const [prefillName, setPrefillName]   = useState('');
  const [organisations, setOrganisations] = useState([]);
  const [persons, setPersons]             = useState([]);

  const defaultSubjectType = personId ? 'person' : 'organisation';

  const [meta, setMeta] = useState({
    name:               '',
    caption:            aiImageCaption || '',
    source_attribution: 'AI-generated image',
    alt_text:           aiImageAltText || '',
    tags:               (aiImageTags || []).join(', '),
    location:           '',
    subject_type:       defaultSubjectType,
    organisation_id:    organisationId || '',
    organisation_name:  organisationName || '',
    person_id:          personId || '',
    person_name:        personName || '',
  });

  useEffect(() => {
    api.get('/organisations').then(r => setOrganisations(r.data)).catch(() => {});
    api.get('/persons').then(r => setPersons(r.data)).catch(() => {});
  }, []);

  const handleGenerate = async () => {
    if (!prompt.trim()) return;
    setGenerating(true);
    setPreview(null);
    try {
      const res = await api.post('/image-gen/generate', { prompt: prompt.trim() });
      setPreview(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Image generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const handleUse = async () => {
    if (!preview) return;
    setSaving(true);
    try {
      const res = await api.post('/image-gen/save', {
        ...preview,
        name:               meta.name,
        caption:            meta.caption,
        source_attribution: meta.source_attribution,
        alt_text:           meta.alt_text,
        tags:               meta.tags,
        location:           meta.location,
        organisation_id:    meta.subject_type === 'organisation' ? (meta.organisation_id || null) : null,
        person_id:          meta.subject_type === 'person' ? (meta.person_id || null) : null,
      });
      onSelect(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save image');
    } finally {
      setSaving(false);
    }
  };

  const updateMeta = (field, value) => setMeta(m => ({ ...m, [field]: value }));

  return (
    <>
      <div className="space-y-4 py-2">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Sparkles size={14} className="text-amber-500" />
          <span>Generate an image using Seedream 5.0 Lite</span>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div className="col-span-2">
            <label className="text-xs font-medium text-foreground">Prompt *</label>
            <textarea
              value={prompt}
              onChange={e => setPrompt(e.target.value)}
              placeholder='e.g. "A busy London financial district skyline at dusk, aerial view"'
              className="mt-1 w-full border border-border rounded-lg px-3 py-2 text-sm bg-background resize-none min-h-[72px] placeholder:text-muted-foreground"
            />
          </div>
          <div className="col-span-2">
            <label className="text-xs font-medium text-foreground">Name</label>
            <input
              className="mt-1 w-full border border-border rounded-lg px-3 py-1.5 text-sm bg-background"
              placeholder="e.g. london-skyline-dusk"
              value={meta.name}
              onChange={e => updateMeta('name', e.target.value)}
            />
          </div>
          <div>
            <label className="text-xs font-medium text-foreground">
              Caption
              {aiImageCaption && <span className="ml-1 text-amber-600 text-xs">(AI suggested)</span>}
            </label>
            <input
              className="mt-1 w-full border border-border rounded-lg px-3 py-1.5 text-sm bg-background"
              placeholder="Image caption"
              value={meta.caption}
              onChange={e => updateMeta('caption', e.target.value)}
            />
          </div>
          <div>
            <label className="text-xs font-medium text-foreground">
              Alt text
              {aiImageAltText && <span className="ml-1 text-amber-600 text-xs">(AI suggested)</span>}
            </label>
            <input
              className="mt-1 w-full border border-border rounded-lg px-3 py-1.5 text-sm bg-background"
              placeholder="Descriptive alt text"
              value={meta.alt_text}
              onChange={e => updateMeta('alt_text', e.target.value)}
            />
          </div>
          <div className="col-span-2">
            <label className="text-xs font-medium text-foreground">
              Tags <span className="text-muted-foreground font-normal">(comma separated)</span>
              {aiImageTags?.length > 0 && <span className="ml-1 text-amber-600 text-xs">(AI suggested)</span>}
            </label>
            <input
              className="mt-1 w-full border border-border rounded-lg px-3 py-1.5 text-sm bg-background"
              placeholder="e.g. finance, london"
              value={meta.tags}
              onChange={e => updateMeta('tags', e.target.value)}
            />
          </div>
          <div>
            <label className="text-xs font-medium text-foreground">Location</label>
            <input
              className="mt-1 w-full border border-border rounded-lg px-3 py-1.5 text-sm bg-background"
              placeholder="e.g. London, UK"
              value={meta.location}
              onChange={e => updateMeta('location', e.target.value)}
            />
          </div>
          <div>
            <label className="text-xs font-medium text-foreground">Accreditation</label>
            <input
              className="mt-1 w-full border border-border rounded-lg px-3 py-1.5 text-sm bg-background"
              placeholder="Accreditation"
              value={meta.source_attribution}
              onChange={e => updateMeta('source_attribution', e.target.value)}
            />
          </div>
          <div className="col-span-2">
            <label className="text-xs font-medium text-foreground mb-1 block">Link to</label>
            <div className="flex gap-2 mb-2">
              <button
                onClick={() => updateMeta('subject_type', 'organisation')}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${meta.subject_type === 'organisation' ? 'bg-[#1F1A17] text-[#FFE4C4] border-[#1F1A17]' : 'bg-background text-muted-foreground border-border hover:bg-muted'}`}
              >
                Organisation
              </button>
              <button
                onClick={() => updateMeta('subject_type', 'person')}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${meta.subject_type === 'person' ? 'bg-[#1F1A17] text-[#FFE4C4] border-[#1F1A17]' : 'bg-background text-muted-foreground border-border hover:bg-muted'}`}
              >
                Person
              </button>
            </div>
            {meta.subject_type === 'organisation' ? (
              <EntitySearchCombobox
                entityType="organisation"
                value={meta.organisation_id ? { id: meta.organisation_id, name: meta.organisation_name } : null}
                onSelect={r => setMeta(m => ({ ...m, organisation_id: r.id, organisation_name: r.name }))}
                onClear={() => setMeta(m => ({ ...m, organisation_id: '', organisation_name: '' }))}
                onCreateRequest={name => { setPrefillName(name); setOrgDialog(true); }}
                placeholder="Search or create organisation..."
              />
            ) : (
              <EntitySearchCombobox
                entityType="person"
                value={meta.person_id ? { id: meta.person_id, name: meta.person_name } : null}
                onSelect={r => setMeta(m => ({ ...m, person_id: r.id, person_name: r.name }))}
                onClear={() => setMeta(m => ({ ...m, person_id: '', person_name: '' }))}
                onCreateRequest={name => { setPrefillName(name); setPersonDialog(true); }}
                placeholder="Search or create person..."
              />
            )}
          </div>
        </div>

        <button
          onClick={handleGenerate}
          disabled={!prompt.trim() || generating}
          className="w-full bg-[#1F1A17] text-[#FFE4C4] rounded-lg py-2.5 text-sm font-medium hover:bg-[#2B241F] disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {generating
            ? <><RefreshCw size={14} className="animate-spin" />Generating...</>
            : <><Sparkles size={14} />Generate Image</>
          }
        </button>

        {preview && (
          <div className="space-y-3 border-t border-border pt-4">
            <img
              src={`data:image/png;base64,${preview.b64_image}`}
              alt="AI generated"
              className="w-full rounded-xl border border-border object-cover aspect-video"
            />
            <div className="flex gap-2">
              <button
                onClick={handleUse}
                disabled={saving}
                className="flex-1 bg-[#1F1A17] text-[#FFE4C4] rounded-lg py-2.5 text-sm font-medium hover:bg-[#2B241F] disabled:opacity-40 flex items-center justify-center gap-2"
              >
                {saving
                  ? <><RefreshCw size={13} className="animate-spin" />Saving...</>
                  : <><Check size={14} />Use this image</>
                }
              </button>
              <button
                onClick={() => setPreview(null)}
                className="flex-1 border border-border rounded-lg py-2 text-sm text-muted-foreground hover:bg-muted"
              >
                Generate another
              </button>
            </div>
          </div>
        )}
      </div>

      <OrgMatchDialog
        open={orgDialog}
        onOpenChange={setOrgDialog}
        prefillName={prefillName}
        onSelect={r => setMeta(m => ({ ...m, organisation_id: r.id, organisation_name: r.name }))}
        setOrganisations={org => setOrganisations(prev => [...prev, org])}
      />
      <PersonMatchDialog
        open={personDialog}
        onOpenChange={setPersonDialog}
        prefillName={prefillName}
        onSelect={r => setMeta(m => ({ ...m, person_id: r.id, person_name: r.name }))}
        organisations={organisations}
        setPersons={p => setPersons(prev => [...prev, p])}
      />
    </>
  );
}

export default function MediaPickerModal({
  open,
  onOpenChange,
  onSelect,
  organisationId,
  organisationName     = '',
  personId,
  personName           = '',
  tags,
  publicationId,
  currentAssetId,
  aiImageCaption       = '',
  aiImageAltText       = '',
  aiImageTags          = [],
  aiImageAccreditation = '',
  aiPromptSuggestion   = '',
  organisations        = [],
}) {
  const [activeTab, setActiveTab]               = useState('suggested');
  const [suggested, setSuggested]               = useState(null);
  const [assets, setAssets]                     = useState([]);
  const [total, setTotal]                       = useState(0);
  const [loading, setLoading]                   = useState(false);
  const [loadingSuggested, setLoadingSuggested] = useState(false);
  const [selected, setSelected]                 = useState(null);
  const [offset, setOffset]                     = useState(0);
  const limit = 18;

  // Browse filters — always reset to empty when modal opens
  const [filters, setFilters] = useState({ ...EMPTY_BROWSE_FILTERS });
  const debouncedSearch = useDebounce(filters.search, 350);
  const debouncedTags   = useDebounce(filters.tags, 350);

  // True while debounce is still counting down — drives the input spinner
  const searchPending = filters.search !== debouncedSearch;
  const tagsPending   = filters.tags   !== debouncedTags;

  // Upload tab state
  const [uploadFiles, setUploadFiles]             = useState([]);
  const [uploadPerFile, setUploadPerFile]         = useState([]);
  const [uploadDragging, setUploadDragging]       = useState(false);
  const [uploading, setUploading]                 = useState(false);
  const [uploadSubjectType, setUploadSubjectType] = useState('organisation');
  const [orgDialog, setOrgDialog]                 = useState(false);
  const [personDialog, setPersonDialog]           = useState(false);
  const [prefillName, setPrefillName]             = useState('');
  const [localOrgs, setLocalOrgs]                 = useState(organisations);
  const [uploadForm, setUploadForm] = useState({
    image_type:        'uploaded',
    organisation_id:   '',
    organisation_name: '',
    person_id:         '',
    person_name:       '',
    tags:              '',
    alt_text:          '',
    location:          '',
    accreditation:     '',
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setLocalOrgs(organisations); }, [JSON.stringify(organisations)]);

  const fetchSuggested = useCallback(async () => {
    setLoadingSuggested(true);
    try {
      const params = {};
      if (organisationId) params.organisation_id = organisationId;
      if (personId)       params.person_id       = personId;
      if (publicationId)  params.publication_id  = publicationId;
      const allTags = [...(tags || []), ...aiImageTags];
      if (personId && personName && allTags.length === 0) allTags.push(personName);
      if (allTags.length) params.tags = [...new Set(allTags)].join(',');
      const res = await mediaApi.assignImage(params);
      setSuggested(res.data.asset || null);
    } catch {
      setSuggested(null);
    } finally {
      setLoadingSuggested(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organisationId, personId, publicationId]);

  // Reset everything when modal opens
  useEffect(() => {
    if (!open) return;
    setSelected(null);
    setActiveTab('suggested');
    setUploadFiles([]);
    setUploadPerFile([]);
    setOffset(0);
    setFilters({ ...EMPTY_BROWSE_FILTERS });
    setUploadSubjectType(personId ? 'person' : 'organisation');
    setUploadForm({
      image_type:        'uploaded',
      organisation_id:   organisationId || '',
      organisation_name: organisationName || '',
      person_id:         personId || '',
      person_name:       personName || '',
      tags:              aiImageTags.join(', '),
      alt_text:          aiImageAltText || '',
      location:          '',
      accreditation:     aiImageAccreditation || '',
    });
    fetchSuggested();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const fetchAssets = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit, offset };
      if (debouncedSearch)     params.search      = debouncedSearch;
      if (filters.image_type)  params.image_type  = filters.image_type;
      if (filters.orientation) params.orientation = filters.orientation;
      if (debouncedTags)       params.tags        = debouncedTags;
      const res = await mediaApi.getAssets(params);
      setAssets(res.data.assets);
      setTotal(res.data.total);
    } catch {
      toast.error('Failed to load media library');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, debouncedTags, filters.image_type, filters.orientation, offset]);

  useEffect(() => {
    if (open && activeTab === 'browse') fetchAssets();
  }, [open, activeTab, fetchAssets]);

  // Helper: set filter and reset to page 1
  const setFilter = (key, value) => {
    setOffset(0);
    setFilters(f => ({ ...f, [key]: value }));
  };

  const handleConfirm = () => {
    if (!selected) return;
    onSelect({
      file_url:       selected.file_url,
      media_asset_id: selected.id,
      caption:        selected.caption || '',
      alt_text:       selected.alt_text || '',
      accreditation:  selected.source_attribution || '',
    });
    onOpenChange(false);
  };

  const addUploadFiles = (newFiles) => {
    setUploadFiles(prev => [...prev, ...newFiles]);
    setUploadPerFile(prev => [...prev, ...newFiles.map(() => ({ caption: aiImageCaption || '', accreditation: '' }))]);
  };

  const handleUploadDrop = (e) => {
    e.preventDefault();
    setUploadDragging(false);
    addUploadFiles(Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/')));
  };
  const handleUploadFileInput = (e) => addUploadFiles(Array.from(e.target.files).filter(f => f.type.startsWith('image/')));

  const removeUploadFile = (i) => {
    setUploadFiles(prev => prev.filter((_, j) => j !== i));
    setUploadPerFile(prev => prev.filter((_, j) => j !== i));
  };
  const updateUploadPerFile = (i, field, value) => {
    setUploadPerFile(prev => prev.map((f, j) => j === i ? { ...f, [field]: value } : f));
  };

  const handleUpload = async () => {
    if (!uploadFiles.length) return;
    setUploading(true);
    let lastAsset = null;

    for (let i = 0; i < uploadFiles.length; i++) {
      const file = uploadFiles[i];
      const pf   = uploadPerFile[i] || {};
      const fd   = new FormData();
      fd.append('file', file);
      fd.append('image_type', uploadForm.image_type);
      fd.append('tags', uploadForm.tags);
      fd.append('alt_text', uploadForm.alt_text || file.name);
      fd.append('location', uploadForm.location);
      fd.append('caption', pf.caption || '');
      fd.append('source_attribution', pf.accreditation || uploadForm.accreditation || '');
      if (uploadSubjectType === 'organisation' && uploadForm.organisation_id) fd.append('organisation_id', uploadForm.organisation_id);
      if (uploadSubjectType === 'person' && uploadForm.person_id)             fd.append('person_id', uploadForm.person_id);
      try { const res = await mediaApi.uploadAsset(fd); lastAsset = res.data; }
      catch { toast.error(`Failed to upload ${file.name}`); }
    }

    setUploading(false);
    if (lastAsset) {
      toast.success(`${uploadFiles.length} file${uploadFiles.length > 1 ? 's' : ''} uploaded`);
      onSelect({
        file_url:       lastAsset.file_url,
        media_asset_id: lastAsset.id,
        caption:        lastAsset.caption || '',
        alt_text:       lastAsset.alt_text || '',
        accreditation:  lastAsset.source_attribution || '',
      });
      onOpenChange(false);
    }
  };

  const totalPages    = Math.ceil(total / limit);
  const page          = Math.floor(offset / limit) + 1;
  const multipleFiles = uploadFiles.length > 1;

  const tabs = [
    { id: 'suggested', label: 'Suggested' },
    { id: 'browse',    label: 'Browse Library' },
    { id: 'upload',    label: 'Upload New' },
    { id: 'generate',  label: 'AI Generate' },
  ];

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="bg-card border-border max-w-3xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-foreground font-outfit">Select Image</DialogTitle>
          </DialogHeader>

          <div className="flex gap-1 bg-[#FFF4E8] p-1 rounded-xl border border-border w-fit">
            {tabs.map(({ id, label }) => (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${
                  activeTab === id
                    ? 'bg-white text-[#1F1A17] shadow-sm border border-[#E7D2B8]'
                    : 'text-[#6E6258] hover:text-[#1F1A17]'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="flex-1 overflow-y-auto min-h-0">

            {/* ── Suggested tab ── */}
            {activeTab === 'suggested' && (
              <div className="space-y-4 py-2">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Sparkles size={14} className="text-amber-500" />
                  <span>Best match based on organisation, person and image tags</span>
                  <button onClick={fetchSuggested} className="ml-auto text-[#6E6258] hover:text-foreground">
                    <RefreshCw size={13} className={loadingSuggested ? 'animate-spin' : ''} />
                  </button>
                </div>
                {loadingSuggested ? (
                  <div className="flex items-center gap-2 justify-center py-12 text-muted-foreground text-sm">
                    <Loader2 size={16} className="animate-spin" /> Finding best match...
                  </div>
                ) : suggested ? (
                  <div className="space-y-3">
                    <div
                      onClick={() => setSelected(suggested)}
                      className={`relative cursor-pointer rounded-xl overflow-hidden border transition-all max-w-sm ${
                        selected?.id === suggested.id ? 'border-blue-500 ring-2 ring-blue-200' : 'border-border hover:border-[#C9B49A]'
                      }`}
                    >
                      <img
                        src={suggested.thumbnail_url || suggested.file_url}
                        alt={suggested.alt_text || suggested.filename}
                        className="w-full aspect-video object-cover"
                        onError={(e) => { e.target.src = '/placeholder-image.png'; }}
                      />
                      {selected?.id === suggested.id && (
                        <div className="absolute inset-0 bg-blue-500/20 flex items-center justify-center">
                          <Check size={24} className="text-blue-600 bg-white rounded-full p-1" />
                        </div>
                      )}
                    </div>
                    <div className="space-y-0.5">
                      <p className="text-sm font-medium text-foreground">{suggested.filename}</p>
                      <p className="text-xs text-muted-foreground">
                        {suggested.orientation} · used {suggested.usage_count} times
                        {suggested.caption && ` · "${suggested.caption}"`}
                      </p>
                      {suggested.source_attribution && (
                        <p className="text-xs text-muted-foreground">Credit: {suggested.source_attribution}</p>
                      )}
                      {suggested.tags?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {suggested.tags.map(tag => (
                            <span key={tag} className="text-xs bg-muted px-1.5 py-0.5 rounded text-muted-foreground">{tag}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12 space-y-2">
                    <ImageIcon size={32} className="text-[#C9B49A] mx-auto" />
                    <p className="text-sm text-muted-foreground">No match found — browse the library or upload a new image</p>
                    <div className="flex gap-2 justify-center">
                      <button onClick={() => setActiveTab('browse')} className="text-sm text-blue-600 hover:underline">Browse library</button>
                      <span className="text-muted-foreground">·</span>
                      <button onClick={() => setActiveTab('upload')} className="text-sm text-blue-600 hover:underline">Upload new</button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── Browse tab ── */}
            {activeTab === 'browse' && (
              <div className="space-y-3 py-2">
                <div className="flex flex-wrap gap-2">

                  {/* Filename search with spinner */}
                  <div className="relative flex-1 min-w-40">
                    <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                      className="w-full pl-8 pr-8 py-2 border border-border rounded-lg text-sm bg-background"
                      placeholder="Search by filename..."
                      value={filters.search}
                      onChange={e => setFilter('search', e.target.value)}
                    />
                    {(searchPending || (loading && filters.search)) && (
                      <Loader2 size={13} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-muted-foreground" />
                    )}
                  </div>

                  {/* Tag search with spinner */}
                  <div className="relative min-w-36">
                    <input
                      className="w-full pl-3 pr-8 py-2 border border-border rounded-lg text-sm bg-background"
                      placeholder="Tags — e.g. finance, Swindon"
                      value={filters.tags}
                      onChange={e => setFilter('tags', e.target.value)}
                    />
                    {(tagsPending || (loading && filters.tags)) && (
                      <Loader2 size={13} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-muted-foreground" />
                    )}
                  </div>

                  <select
                    className="border border-border rounded-lg px-3 py-2 text-sm bg-background"
                    value={filters.image_type}
                    onChange={e => setFilter('image_type', e.target.value)}
                  >
                    <option value="">All types</option>
                    {IMAGE_TYPES.map(t => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
                  </select>
                  <select
                    className="border border-border rounded-lg px-3 py-2 text-sm bg-background"
                    value={filters.orientation}
                    onChange={e => setFilter('orientation', e.target.value)}
                  >
                    <option value="">All orientations</option>
                    <option value="landscape">Landscape</option>
                    <option value="portrait">Portrait</option>
                    <option value="square">Square</option>
                  </select>
                </div>

                {/* OR hint when tag filter is active */}
                {filters.tags && (
                  <p className="text-xs text-muted-foreground">
                    Showing images tagged with{' '}
                    {filters.tags.split(',').map(t => t.trim()).filter(Boolean).map((t, idx, arr) => (
                      <span key={t}>
                        <span className="font-medium text-foreground">"{t}"</span>
                        {idx < arr.length - 1 && <span className="text-muted-foreground"> or </span>}
                      </span>
                    ))}
                    {(tagsPending || loading) && <Loader2 size={11} className="inline ml-1 animate-spin" />}
                  </p>
                )}

                <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                  {total} asset{total !== 1 ? 's' : ''}
                  {(loading || searchPending) && !filters.tags && <Loader2 size={11} className="animate-spin" />}
                </p>

                {loading && assets.length === 0 ? (
                  <div className="flex items-center gap-2 justify-center py-12 text-muted-foreground text-sm">
                    <Loader2 size={16} className="animate-spin" /> Loading...
                  </div>
                ) : assets.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground text-sm">No assets found</div>
                ) : (
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                    {assets.map(asset => (
                      <AssetThumb
                        key={asset.id}
                        asset={asset}
                        selected={selected?.id === asset.id}
                        onSelect={setSelected}
                      />
                    ))}
                  </div>
                )}
                {totalPages > 1 && (
                  <div className="flex items-center gap-2 justify-center pt-2">
                    <button
                      disabled={page === 1}
                      onClick={() => setOffset(o => Math.max(0, o - limit))}
                      className="px-3 py-1.5 border border-border rounded-lg text-sm disabled:opacity-40 hover:bg-muted"
                    >
                      Previous
                    </button>
                    <span className="text-sm text-muted-foreground">{page} of {totalPages}</span>
                    <button
                      disabled={page === totalPages}
                      onClick={() => setOffset(o => o + limit)}
                      className="px-3 py-1.5 border border-border rounded-lg text-sm disabled:opacity-40 hover:bg-muted"
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* ── Upload tab ── */}
            {activeTab === 'upload' && (
              <div className="space-y-4 py-2">
                {aiImageCaption && (
                  <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                    <Sparkles size={13} className="text-amber-500 shrink-0" />
                    <p className="text-xs text-amber-700">Fields pre-filled from AI draft — review and adjust before uploading</p>
                  </div>
                )}

                <div
                  onDragOver={(e) => { e.preventDefault(); setUploadDragging(true); }}
                  onDragLeave={() => setUploadDragging(false)}
                  onDrop={handleUploadDrop}
                  className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${uploadDragging ? 'border-blue-400 bg-blue-50' : 'border-border hover:border-[#C9B49A]'}`}
                >
                  <Upload size={28} className="mx-auto mb-2 text-muted-foreground" />
                  <p className="text-sm font-medium text-foreground">Drop images here or click to browse</p>
                  <p className="text-xs text-muted-foreground mt-1">JPEG, PNG, WebP, GIF, AVIF, HEIC, TIFF, BMP, SVG — max 50MB</p>
                  <label className="mt-3 inline-block cursor-pointer">
                    <input type="file" multiple accept="image/*" onChange={handleUploadFileInput} className="hidden" />
                    <span className="mt-2 inline-block px-4 py-1.5 bg-[#FFE4C4] text-[#1F1A17] rounded-lg text-sm font-medium hover:bg-[#FFD4A8]">Browse files</span>
                  </label>
                </div>

                {uploadFiles.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground">
                      {uploadFiles.length} file{uploadFiles.length > 1 ? 's' : ''} selected
                      {multipleFiles && ' — caption & credit per image'}
                    </p>
                    {uploadFiles.map((file, i) => {
                      const previewUrl = URL.createObjectURL(file);
                      return (
                        <div key={i} className="bg-muted rounded-xl overflow-hidden border border-border">
                          <div className="flex items-center gap-3 px-3 py-2 border-b border-border">
                            <span className="text-sm text-foreground flex-1 truncate font-medium">{file.name}</span>
                            <span className="text-xs text-muted-foreground">{(file.size / 1024).toFixed(0)}KB</span>
                            <button onClick={() => removeUploadFile(i)} className="text-muted-foreground hover:text-red-500"><X size={13} /></button>
                          </div>
                          <div className="flex gap-3 p-3">
                            <img
                              src={previewUrl}
                              alt={file.name}
                              className="w-24 h-16 object-cover rounded-lg shrink-0 border border-border"
                              onLoad={() => URL.revokeObjectURL(previewUrl)}
                            />
                            <div className="flex-1 space-y-2">
                              <div>
                                <label className="text-xs text-muted-foreground mb-1 block">Caption</label>
                                <input
                                  className="w-full border border-border rounded-lg px-2.5 py-1.5 text-sm bg-background"
                                  placeholder="Image caption"
                                  value={uploadPerFile[i]?.caption || ''}
                                  onChange={e => updateUploadPerFile(i, 'caption', e.target.value)}
                                />
                              </div>
                              <div>
                                <label className="text-xs text-muted-foreground mb-1 block">
                                  Accreditation <span className="font-normal opacity-70">— overrides default</span>
                                </label>
                                <input
                                  className="w-full border border-border rounded-lg px-2.5 py-1.5 text-sm bg-background"
                                  placeholder="Leave blank to use default below"
                                  value={uploadPerFile[i]?.accreditation || ''}
                                  onChange={e => updateUploadPerFile(i, 'accreditation', e.target.value)}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-foreground">Type</label>
                    <select
                      className="mt-1 w-full border border-border rounded-lg px-3 py-2 text-sm bg-background"
                      value={uploadForm.image_type}
                      onChange={e => setUploadForm(f => ({ ...f, image_type: e.target.value }))}
                    >
                      {IMAGE_TYPES.map(t => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
                    </select>
                  </div>

                  <div className="col-span-2">
                    <label className="text-xs font-medium text-foreground mb-1 block">Link to</label>
                    <div className="flex gap-2 mb-2">
                      <button
                        onClick={() => setUploadSubjectType('organisation')}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${uploadSubjectType === 'organisation' ? 'bg-[#1F1A17] text-[#FFE4C4] border-[#1F1A17]' : 'bg-background text-muted-foreground border-border hover:bg-muted'}`}
                      >
                        Organisation
                      </button>
                      <button
                        onClick={() => setUploadSubjectType('person')}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${uploadSubjectType === 'person' ? 'bg-[#1F1A17] text-[#FFE4C4] border-[#1F1A17]' : 'bg-background text-muted-foreground border-border hover:bg-muted'}`}
                      >
                        Person
                      </button>
                    </div>
                    {uploadSubjectType === 'organisation' ? (
                      <EntitySearchCombobox
                        entityType="organisation"
                        value={uploadForm.organisation_id ? { id: uploadForm.organisation_id, name: uploadForm.organisation_name } : null}
                        onSelect={r => setUploadForm(f => ({ ...f, organisation_id: r.id, organisation_name: r.name }))}
                        onClear={() => setUploadForm(f => ({ ...f, organisation_id: '', organisation_name: '' }))}
                        onCreateRequest={name => { setPrefillName(name); setOrgDialog(true); }}
                        placeholder="Search or create organisation..."
                      />
                    ) : (
                      <EntitySearchCombobox
                        entityType="person"
                        value={uploadForm.person_id ? { id: uploadForm.person_id, name: uploadForm.person_name } : null}
                        onSelect={r => setUploadForm(f => ({ ...f, person_id: r.id, person_name: r.name }))}
                        onClear={() => setUploadForm(f => ({ ...f, person_id: '', person_name: '' }))}
                        onCreateRequest={name => { setPrefillName(name); setPersonDialog(true); }}
                        placeholder="Search or create person..."
                      />
                    )}
                  </div>

                  <div className="col-span-2">
                    <label className="text-xs font-medium text-foreground">
                      Tags
                      {aiImageTags.length > 0 && <span className="ml-1 text-amber-600 text-xs">(AI suggested)</span>}
                      {multipleFiles && <span className="ml-1 text-muted-foreground font-normal text-xs">— shared</span>}
                    </label>
                    <input
                      className="mt-1 w-full border border-border rounded-lg px-3 py-2 text-sm bg-background"
                      placeholder="e.g. technology, samsung"
                      value={uploadForm.tags}
                      onChange={e => setUploadForm(f => ({ ...f, tags: e.target.value }))}
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="text-xs font-medium text-foreground">
                      Alt text
                      {aiImageAltText && <span className="ml-1 text-amber-600 text-xs">(AI suggested)</span>}
                      {multipleFiles && <span className="ml-1 text-muted-foreground font-normal text-xs">— shared</span>}
                    </label>
                    <input
                      className="mt-1 w-full border border-border rounded-lg px-3 py-2 text-sm bg-background"
                      placeholder="Descriptive alt text"
                      value={uploadForm.alt_text}
                      onChange={e => setUploadForm(f => ({ ...f, alt_text: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-foreground">
                      Location
                      {multipleFiles && <span className="ml-1 text-muted-foreground font-normal text-xs">— shared</span>}
                    </label>
                    <input
                      className="mt-1 w-full border border-border rounded-lg px-3 py-2 text-sm bg-background"
                      placeholder="e.g. London, UK"
                      value={uploadForm.location}
                      onChange={e => setUploadForm(f => ({ ...f, location: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-foreground">
                      Accreditation
                      {aiImageAccreditation && <span className="ml-1 text-amber-600 text-xs">(AI suggested)</span>}
                      {multipleFiles && <span className="ml-1 text-muted-foreground font-normal text-xs">— default for all</span>}
                    </label>
                    <input
                      className="mt-1 w-full border border-border rounded-lg px-3 py-2 text-sm bg-background"
                      placeholder={multipleFiles ? 'Applied to images with no credit set above' : 'Accreditation'}
                      value={uploadForm.accreditation || ''}
                      onChange={e => setUploadForm(f => ({ ...f, accreditation: e.target.value }))}
                    />
                  </div>
                </div>

                <button
                  onClick={handleUpload}
                  disabled={!uploadFiles.length || uploading}
                  className="w-full bg-[#1F1A17] text-[#FFE4C4] rounded-lg py-2.5 text-sm font-medium hover:bg-[#2B241F] disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {uploading
                    ? <><RefreshCw size={14} className="animate-spin" />Uploading...</>
                    : <><Upload size={14} />Upload and select</>
                  }
                </button>
              </div>
            )}

            {/* ── AI Generate tab ── */}
            {activeTab === 'generate' && (
              <AIGenerateTab
                aiImageCaption={aiImageCaption}
                aiImageAltText={aiImageAltText}
                aiImageTags={aiImageTags}
                aiImageAccreditation={aiImageAccreditation}
                aiPromptSuggestion={aiPromptSuggestion}
                organisationId={organisationId}
                organisationName={organisationName}
                personId={personId}
                personName={personName}
                onSelect={(asset) => {
                  onSelect({
                    file_url:       asset.file_url,
                    media_asset_id: asset.id,
                    caption:        asset.caption || '',
                    alt_text:       asset.alt_text || '',
                    accreditation:  asset.source_attribution || '',
                  });
                  onOpenChange(false);
                }}
              />
            )}
          </div>

          {/* Footer */}
          {activeTab !== 'upload' && activeTab !== 'generate' && (
            <div className="flex items-center justify-between pt-4 border-t border-border mt-2">
              <p className="text-sm text-muted-foreground">
                {selected ? `Selected: ${selected.filename}` : 'No image selected'}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => onOpenChange(false)}
                  className="px-4 py-2 border border-border rounded-lg text-sm text-muted-foreground hover:bg-muted"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirm}
                  disabled={!selected}
                  className="px-4 py-2 bg-[#1F1A17] text-[#FFE4C4] rounded-lg text-sm font-medium hover:bg-[#2B241F] disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Use this image
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <OrgMatchDialog
        open={orgDialog}
        onOpenChange={setOrgDialog}
        prefillName={prefillName}
        onSelect={r => setUploadForm(f => ({ ...f, organisation_id: r.id, organisation_name: r.name }))}
        setOrganisations={org => setLocalOrgs(prev => [...prev, org])}
      />
      <PersonMatchDialog
        open={personDialog}
        onOpenChange={setPersonDialog}
        prefillName={prefillName}
        onSelect={r => setUploadForm(f => ({ ...f, person_id: r.id, person_name: r.name }))}
        organisations={localOrgs}
        setPersons={() => {}}
      />
    </>
  );
}